/**
 * Single source of truth for the runtime "is this production?" flag.
 *
 * On the frontend the environment is signalled by `VITE_ENVIRONMENT` (a Vite
 * build-time var), whereas backend code and Cloudflare workers read the plain
 * `ENVIRONMENT` binding instead. This module covers the frontend signal only:
 * it computes a boolean from `VITE_ENVIRONMENT` so app code can branch on a
 * named constant rather than re-deriving the comparison in each call site.
 *
 * Resolution is intentionally fail-safe: the default is `staging` (this repo
 * is the staging codebase per `CLAUDE.md`), so an unset or unrecognised value
 * yields `IS_PROD === false`. The comparison is case-insensitive.
 *
 * Note: `import.meta.env.VITE_ENVIRONMENT === 'production'` written as a
 * literal is also used in a couple of places (e.g. `components/AppRouter.tsx`)
 * specifically so Vite can tree-shake dev-only routes out of the prod bundle.
 * That literal form is the build-time equivalent of this runtime flag, the two
 * must not be conflated: keep the literal where tree-shaking depends on it and
 * use `IS_PROD` everywhere a plain runtime check is wanted.
 *
 * @module lib/utils/env/is-prod
 */
import { getEnv } from './index';

/**
 * True when the frontend is running against the production environment.
 *
 * Computed once at module evaluation from `VITE_ENVIRONMENT`, defaulting to
 * `staging` when the var is absent. Case-insensitive: `production` and
 * `PRODUCTION` both resolve to `true`.
 */
export const IS_PROD: boolean =
  getEnv('VITE_ENVIRONMENT', false, 'staging')!.toLowerCase() === 'production';
